/**
 * Shared helper functions for download/export functionality
 */

import { ToastrService } from '../services/toastr-service';

/**
 * Handle the download of a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = globalThis.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  globalThis.URL.revokeObjectURL(url);
}

/**
 * Handle download errors with proper user feedback
 */
export async function handleDownloadError(
  err: any,
  toastrService: ToastrService,
  defaultMessage: string = 'Failed to download file',
): Promise<void> {
  console.error('Error downloading file:', err);

  const showError = (message: string) => {
    toastrService.showToastr(message, 'error');
  };

  if (err?.error instanceof Blob) {
    try {
      const text = await err.error.text();
      try {
        const parsed = JSON.parse(text);
        showError(parsed?.error ?? defaultMessage);
      } catch {
        showError(defaultMessage);
      }
    } catch {
      showError(defaultMessage);
    }
  } else {
    showError(defaultMessage);
  }
}

/**
 * Generate a filename with current date
 */
export function generateDateFilename(prefix: string, extension: string = 'xlsx'): string {
  const date = new Date().toISOString().split('T')[0];
  return `${prefix}-${date}.${extension}`;
}

/**
 * Quote a single CSV cell. Values are never prefixed to defuse formulas — the cells are
 * re-used to correct and re-upload the source file, so they have to round-trip unchanged.
 */
export function toCsvCell(value: string | number | null | undefined): string {
  return `"${String(value ?? '').replaceAll('"', '""')}"`;
}

/**
 * Build a CSV document from a header row and its data rows.
 */
export function buildCsv(header: string[], rows: (string | number | null | undefined)[][]): string {
  return [header, ...rows].map((row) => row.map(toCsvCell).join(',')).join('\r\n');
}

/**
 * Download a CSV string as a file. The BOM keeps Excel on UTF-8.
 */
export function downloadCsv(csv: string, filename: string): void {
  const byteOrderMark = '\uFEFF';
  downloadBlob(new Blob([byteOrderMark + csv], { type: 'text/csv;charset=utf-8;' }), filename);
}
